import { daysUntilCalendar, formatDateOnly } from './dateOnly';

export function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Render a DATE-ONLY value (deadline, award date, due date…). Delegates
 * to lib/dateOnly: the day shown is the value's calendar date — read in
 * its explicit timezone when one is supplied, else its UTC date — never
 * the viewer's local rendering of the instant. (The previous
 * `toLocaleDateString` in the browser zone rendered a UTC-midnight
 * Oct 24 award date as Oct 23 in US timezones — R2-BR-D17.)
 */
export function formatDate(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) return '—';
  return formatDateOnly(iso, { timeZone, style: 'short' }) ?? '—';
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Date-only rendering of an INSTANT (a timestamp with a time-of-day,
 * e.g. a Stripe subscription period end). RC-2026-10-26.11 (D80): the
 * Individual profile rendered its period end with lib/dateOnly's
 * formatDateOnly, which — given no explicit zone — reads the UTC
 * calendar date; a period ending Oct 30, 2027 00:40 UTC therefore
 * displayed "Oct 30, 2027" to a Central-time user whose actual last
 * day (and Stripe's displayed next billing date) is Oct 29. An instant
 * renders in the viewer's own zone here, matching how Stripe presents
 * the same moment; date-only FACTS (deadlines, award dates) keep
 * using lib/dateOnly via formatDate above.
 */
export function formatInstantDate(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/**
 * Whole CALENDAR days from today until the ISO date (negative =
 * overdue). Calendar arithmetic via lib/dateOnly — the previous
 * `Math.ceil` on the instant difference disagreed with the scoring
 * engine's `Math.floor` (53 vs 54 days overdue on one page, R2-BR-D17)
 * and flipped with the time of day.
 */
export function daysUntil(iso: string | null | undefined, timeZone?: string | null): number | null {
  if (!iso) return null;
  return daysUntilCalendar(iso, new Date(), timeZone);
}

/* ---------- Official deadline rendering (explicit timezone) ---------- */

/** Sources whose notices publish deadlines in US Eastern time. */
const FEDERAL_EASTERN_SOURCES = new Set(['sam.gov', 'grants.gov', 'sbir.gov', 'sbir']);

/** Normalize an Intl short zone name ("EST", "PDT") to the generic "ET"/"PT" style. */
function genericZoneAbbrev(abbrev: string): string {
  const map: Record<string, string> = {
    EST: 'ET', EDT: 'ET',
    CST: 'CT', CDT: 'CT',
    MST: 'MT', MDT: 'MT',
    PST: 'PT', PDT: 'PT',
    AKST: 'AKT', AKDT: 'AKT',
    HST: 'HT', HDT: 'HT',
  };
  return map[abbrev] ?? abbrev;
}

function zoneAbbrev(iso: string, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date(iso));
    const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return genericZoneAbbrev(name);
  } catch {
    return '';
  }
}

/**
 * Which zone an official deadline should be read in: the opportunity's own
 * zone when the backend supplies one, else America/New_York for federal
 * sources, else the viewer's local zone.
 */
export function resolveDeadlineZone(
  tz: string | null | undefined,
  source: string | null | undefined,
): { timeZone: string; viewerLocal: boolean } {
  if (tz) {
    try {
      // Throws on an unknown IANA name — fall through to the defaults.
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return { timeZone: tz, viewerLocal: false };
    } catch {
      /* unknown zone — use fallbacks */
    }
  }
  if (source && FEDERAL_EASTERN_SOURCES.has(source.trim().toLowerCase())) {
    return { timeZone: 'America/New_York', viewerLocal: false };
  }
  return {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewerLocal: true,
  };
}

/**
 * Render an official response deadline. When the record carries an
 * explicit timezone it is a timed instant and renders with its time in
 * that zone, e.g. "Mar 14, 2026 · 5:00 PM ET". Without one it is
 * date-only and renders as a plain calendar date with no time
 * (R2-BR-D17). Returns '—' for missing/invalid input.
 */
export function formatOfficialDeadline(
  iso: string | null | undefined,
  opts?: { tz?: string | null; source?: string | null },
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // R2-BR-D17: with no explicit timezone on the record the deadline is
  // DATE-ONLY — render its calendar date and NO time. The old fallback
  // rendered the stored midnight through the viewer's zone and invented
  // a time ("Sep 1, 2026 · 12:00 AM CT (your local time)").
  if (!opts?.tz) {
    return formatDateOnly(iso, { style: 'short' }) ?? '—';
  }
  const { timeZone, viewerLocal } = resolveDeadlineZone(opts?.tz, opts?.source);
  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
  const timePart = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  const abbrev = zoneAbbrev(iso, timeZone);
  const zoneLabel = viewerLocal
    ? `${abbrev} (your local time)`
    : abbrev;
  return `${datePart} · ${timePart}${zoneLabel ? ` ${zoneLabel}` : ''}`;
}

/* ---------- Deadline states (release acceptance) ---------- */

/**
 * The label for a NON-dated deadline state — the one shared vocabulary
 * every surface uses, replacing the five different null labels the
 * product grew ("No deadline", "Not set", "Not published", "—",
 * silence). Returns null for DATED (and for legacy rows without a
 * kind): the caller renders the actual date as before.
 *   ROLLING     — open/rolling intake; there is no closing date.
 *   UNPUBLISHED — announced, but no closing date published yet.
 *   UNKNOWN     — nothing captured; the honest label says where to look.
 */
export function deadlineStateLabel(kind: string | null | undefined): string | null {
  switch (kind) {
    case 'ROLLING':
      return 'Rolling — no deadline';
    case 'UNPUBLISHED':
      return 'Deadline not yet published';
    case 'UNKNOWN':
      return 'Deadline unknown — confirm on the source site';
    default:
      return null;
  }
}

/* ---------- Opportunity value (budget-range meaning) ---------- */

/**
 * Render an opportunity's estimated value PRESERVING the source
 * meaning (release-acceptance budget audit): a stored range renders
 * as the range, a point value (min == max) as that exact figure, a
 * ceiling-only value as "Up to $X" (the ceiling is a cap, not the
 * value — the RC.2 residual fixed in Phase A.4), a floor-only value
 * as "$X+", a genuine $0 as "$0", and nothing stored as the caller's
 * fallback. Every org surface renders this one label (Discover card
 * + preview pane, OpportunityDetail header/summary/funding details,
 * Saved, Pipeline, Portfolio, Proposals, ProposalWorkspace header,
 * Concierge cards, the Opportunities table), so the branches here
 * are the wording everywhere. (The previous per-page helpers rendered
 * any range as its ceiling alone.)
 */
export function opportunityValueLabel(
  o: { estValueMin?: number | null; estValueMax?: number | null },
  fallback: string,
): string {
  const mn = o.estValueMin ?? null;
  const mx = o.estValueMax ?? null;
  if (mn != null && mx != null) return mn === mx ? formatMoney(mn) : `${formatMoney(mn)} – ${formatMoney(mx)}`;
  if (mx != null) return mx === 0 ? formatMoney(mx) : `Up to ${formatMoney(mx)}`;
  if (mn != null) return `${formatMoney(mn)}+`;
  return fallback;
}

/** Compact relative time for freshness labels: "just now", "5m ago", "3h ago", "2d ago". */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** "Oct 2026" style month label for grouping. */
export function monthKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Named HTML entities that appear in third-party data (e.g. Grants.gov titles). */
const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ldquo: '“',
  rdquo: '”',
  lsquo: '‘',
  rsquo: '’',
  laquo: '«',
  raquo: '»',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  middot: '·',
  bull: '•',
  copy: '©',
  reg: '®',
  trade: '™',
  deg: '°',
  plusmn: '±',
  sect: '§',
};

/**
 * Decode HTML entities in a string without touching the DOM.
 * Unknown entities are left untouched. Decoded in a single pass so
 * already-encoded input (e.g. "&amp;mdash;") is only decoded once.
 */
export function decodeHtmlEntities(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/&#(\d+);/g, (_m, code) => {
      const n = Number(code);
      return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : _m;
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => {
      const n = parseInt(hex, 16);
      return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : _m;
    })
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (m, name: string) => NAMED_HTML_ENTITIES[name] ?? m);
}
